import { ProductExclusion as ProductExclusionNode, ProductExclusionReason } from "@acto/gql/graphql";
import { addDaysUtc, isoToUtc } from "@acto/web-common/dates";
import LocalizedStringMap from "./LocalizedStringMap";

/** `lastDay` is inclusive: the hide still holds on that day. */
export type HideDuration = { kind: "forever" } | { kind: "until"; lastDay: Date };

/** The api only ever writes midnight, so the day before validTo's date is the last held day. */
export const durationOf = (validTo: string | null | undefined): HideDuration => {
  if (!validTo) return { kind: "forever" };

  const [datePart] = validTo.split("T");

  return { kind: "until", lastDay: addDaysUtc(isoToUtc(datePart), -1) };
};

type ProductExclusionFields = Pick<
  ProductExclusionNode,
  "id" | "clientId" | "productId" | "productName" | "reason" | "reasonText" | "validTo"
>;

export default class ProductExclusion {
  constructor(
    public readonly id: string,
    public readonly clientId: string,
    public readonly productId: string,
    public readonly productNames: LocalizedStringMap,
    public readonly reason: ProductExclusionReason,
    public readonly reasonText: string | null,
    public readonly validTo: string | null,
  ) {}

  static fromGraphql(data: ProductExclusionFields): ProductExclusion {
    return new ProductExclusion(
      data.id,
      data.clientId,
      data.productId,
      LocalizedStringMap.fromGraphql(data.productName),
      data.reason,
      data.reasonText ?? null,
      data.validTo ?? null,
    );
  }

  /** Null when the catalog no longer carries the product. */
  name(locale: string): string | null {
    return this.productNames.hasTranslations() ? this.productNames.getTranslation(locale) : null;
  }

  duration(): HideDuration {
    return durationOf(this.validTo);
  }
}
